import { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, X, Check, ArrowRight, Plus, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from "@/lib/utils";
import useApi from '../../api';
import { AttioFieldSelect, AttioField } from "./AttioFieldSelect";
import { AttioDefaultField } from "./AttioDefaultField";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import AttioIcon from '../../icons/attio_logo.png';

interface SystemField {
    name: string;
    label: string;
    description: string;
}

interface AttioFieldsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    formFields: any[];
    onSave: (fieldMappings: Record<string, string>, defaultFields: Record<string, string>) => void;
    existingMappings?: Record<string, string>;
    defaultFields?: Record<string, string>;
    integrationId: string;
}

const SYSTEM_FIELDS: SystemField[] = [
    { name: 'aimdoc_parent_url', label: 'Source URL', description: 'URL where the form was submitted' },
    { name: 'aimdoc_referrer', label: 'Referrer', description: 'First external page that referred the visitor' },
    { name: 'city', label: 'City', description: 'Visitor city' },
    { name: 'region', label: 'Region', description: 'Visitor region/state' },
    { name: 'country', label: 'Country', description: 'Visitor country' }
];

const RESERVED_ATTIO_PERSON_FIELDS = new Set(['name', 'email_addresses']);
const RESERVED_AIMDOC_PERSON_FIELDS = new Set(['first_name', 'last_name', 'email']);
const AUTO_MAPPED_ATTIO_TARGETS: Record<string, string> = {
    first_name: 'Name',
    last_name: 'Name',
    email: 'Email addresses',
};

const cleanReservedFieldMappings = (mappings: Record<string, string>) => {
    return Object.fromEntries(
        Object.entries(mappings || {}).filter(
            ([source, target]) =>
                !RESERVED_AIMDOC_PERSON_FIELDS.has(source) && !RESERVED_ATTIO_PERSON_FIELDS.has(target)
        )
    );
};

const cleanReservedDefaultFields = (defaults: Record<string, string>) => {
    return Object.fromEntries(
        Object.entries(defaults || {}).filter(([target]) => !RESERVED_ATTIO_PERSON_FIELDS.has(target))
    );
};

export default function AttioFieldsModal({
    isOpen,
    closeModal,
    formFields,
    onSave,
    existingMappings = {},
    defaultFields = {},
    integrationId
}: AttioFieldsModalProps) {
    const { get } = useApi();
    const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(existingMappings);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [attioFields, setAttioFields] = useState<AttioField[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [defaultFieldMappings, setDefaultFieldMappings] = useState<Record<string, string>>(defaultFields);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (isOpen) {
            setFieldMappings(cleanReservedFieldMappings(existingMappings));
            setDefaultFieldMappings(cleanReservedDefaultFields(defaultFields));
            setExpandedSections({});
            void fetchAttioFields();
        }
        // Only initialize from saved props when the modal opens or the integration changes.
        // Parent re-renders can create fresh mapping objects and should not reset in-progress edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, integrationId]);

    const fetchAttioFields = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await get(`/integrations/${integrationId}/contact-fields?object=people`);
            if (response.data.status === 'success') {
                setAttioFields(response.data.fields || []);
            } else {
                setError('Failed to load Attio attributes');
            }
        } catch (err) {
            setError('Failed to load Attio attributes');
            console.error('Error fetching Attio attributes:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const cleanDefaultFields = (defaults: Record<string, string>) => {
        const next = { ...(defaults || {}) };
        delete next[""];
        Object.keys(next).forEach((k) => {
            if (!k) delete next[k];
        });
        return next;
    };

    const formFieldNameSet = useMemo(() => {
        const set = new Set<string>();
        (formFields || []).forEach((f: any) => {
            const name = typeof f?.name === 'string' ? f.name : '';
            if (name) set.add(name);
        });
        SYSTEM_FIELDS.forEach((field) => {
            if (field.name) set.add(field.name);
        });
        return set;
    }, [formFields]);

    const visibleFormFields = useMemo(() => {
        return (formFields || []).filter((field: any) => {
            const name = typeof field?.name === 'string' ? field.name : '';
            return Boolean(name);
        });
    }, [formFields]);

    const autoMappedFormFieldCount = useMemo(() => {
        return visibleFormFields.filter((field: any) => RESERVED_AIMDOC_PERSON_FIELDS.has(field.name)).length;
    }, [visibleFormFields]);

    const orphanedMappingKeys = useMemo(() => {
        return Object.keys(fieldMappings || {}).filter((k) => !formFieldNameSet.has(k));
    }, [fieldMappings, formFieldNameSet]);

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const handleFieldSelect = (fieldName: string) => {
        setSelectedField(fieldName);
    };

    const handleAttioFieldSelect = (attioFieldName: string) => {
        if (!selectedField) return;
        setFieldMappings(prev => ({
            ...prev,
            [selectedField]: attioFieldName
        }));
        setSelectedField(null);
    };

    const handleClearMapping = (fieldName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const next = { ...fieldMappings };
        delete next[fieldName];
        setFieldMappings(next);
    };

    const handleSaveAll = () => {
        const cleanedMappings = cleanReservedFieldMappings(fieldMappings);
        const cleanedDefaults = cleanDefaultFields(cleanReservedDefaultFields(defaultFieldMappings));
        onSave(cleanedMappings, cleanedDefaults);
        closeModal();
    };

    const handleDefaultFieldUpdate = (attioField: string, value: string) => {
        setDefaultFieldMappings(prev => {
            const next = { ...prev };
            delete next[""];
            next[attioField] = value;
            return next;
        });
    };

    const handleRemoveDefaultField = (attioField: string) => {
        const next = { ...defaultFieldMappings };
        delete next[attioField];
        setDefaultFieldMappings(next);
    };

    const handleAddDefaultField = () => {
        setDefaultFieldMappings(prev => ({
            ...prev,
            "": ""
        }));
    };

    const getMappedCount = () => Object.keys(fieldMappings).length;
    const getDefaultCount = () => Object.keys(cleanDefaultFields(defaultFieldMappings)).length;
    const hasAnyConfiguration = () => getMappedCount() > 0 || getDefaultCount() > 0;

    const getAttioFieldLabel = (fieldName: string) => {
        const field = attioFields.find(f => f.name === fieldName);
        return field ? field.label : fieldName;
    };

    const getDisplayedAttioFieldLabel = (fieldName: string) => {
        return AUTO_MAPPED_ATTIO_TARGETS[fieldName] || getAttioFieldLabel(fieldName);
    };

    if (error && !isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={closeModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="h-10 w-10 rounded-lg bg-red-500/15 flex items-center justify-center">
                                <AlertCircle className="h-5 w-5 text-red-500" />
                            </div>
                            <DialogTitle>Connection Error</DialogTitle>
                        </div>
                        <DialogDescription className="text-destructive">
                            {error}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeModal}>Close</Button>
                        <Button onClick={fetchAttioFields}>Retry</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
                <div className="px-6 py-5 border-b bg-muted/30">
                    <DialogHeader className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-background border shadow-sm flex items-center justify-center">
                                <img src={AttioIcon} alt="Attio" className="h-6" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl">Attio People Sync</DialogTitle>
                                <DialogDescription className="text-sm mt-0.5">
                                    Map your form fields to Attio person attributes
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                </div>

                <ScrollArea className="flex-1">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                            <Loader2 className="h-8 w-8 animate-spin mb-3" />
                            <span className="text-sm">Loading Attio attributes...</span>
                        </div>
                    ) : (
                        <div className="p-6 space-y-6">
                            <div className="rounded-xl border bg-card p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold">People Attributes</h3>
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                            Map fields to sync visitor data to Attio
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            Email and full name sync automatically from Aimdoc's standard fields.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {getMappedCount() > 0 && (
                                            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                                                <Check className="h-3 w-3 mr-1" />
                                                {getMappedCount()} mapped
                                            </Badge>
                                        )}
                                        {getDefaultCount() > 0 && (
                                            <Badge variant="secondary">
                                                {getDefaultCount()} defaults
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {orphanedMappingKeys.length > 0 ? (
                                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                                        <div className="flex-1 min-w-0 space-y-2">
                                            <div className="font-semibold">Orphaned mappings</div>
                                            <div className="text-sm text-muted-foreground">
                                                These mappings reference form fields that no longer exist in Data Capture. Remove them to prevent sync errors.
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {orphanedMappingKeys.map((k) => (
                                                    <Badge key={k} variant="secondary" className="gap-1">
                                                        <span className="font-mono text-xs">{k}</span>
                                                        <button
                                                            type="button"
                                                            className="ml-1 rounded hover:bg-muted px-0.5"
                                                            onClick={() => {
                                                                setFieldMappings(prev => {
                                                                    const next = { ...(prev || {}) };
                                                                    delete next[k];
                                                                    return next;
                                                                });
                                                            }}
                                                            aria-label={`Remove orphaned mapping ${k}`}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            <div className="rounded-xl border bg-card overflow-hidden">
                                <Collapsible
                                    open={expandedSections['form'] !== false}
                                    onOpenChange={() => toggleSection('form')}
                                >
                                    <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            {expandedSections['form'] === false ? (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <h4 className="font-medium">Form Fields</h4>
                                            <Badge variant="secondary" className="text-[10px]">
                                                {visibleFormFields.length}
                                            </Badge>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            {Object.keys(fieldMappings).filter(k => visibleFormFields.some(f => f.name === k)).length + autoMappedFormFieldCount} mapped
                                        </span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="px-5 pb-5 space-y-2">
                                            {visibleFormFields.map((field) => {
                                                const isAutoMapped = RESERVED_AIMDOC_PERSON_FIELDS.has(field.name);
                                                const isMapped = isAutoMapped || Boolean(fieldMappings[field.name]);
                                                const isSelected = selectedField === field.name;
                                                return (
                                                    <div
                                                        key={field.name}
                                                        className={cn(
                                                            'rounded-lg border transition-all duration-200',
                                                            isSelected
                                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                                : isMapped
                                                                    ? 'border-emerald-500/30 bg-emerald-500/10'
                                                                    : 'hover:border-muted-foreground/30'
                                                        )}
                                                    >
                                                        <div
                                                            className={cn("p-3", !isAutoMapped && "cursor-pointer")}
                                                            onClick={() => {
                                                                if (!isAutoMapped) handleFieldSelect(field.name);
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={cn(
                                                                        "h-8 w-8 rounded-lg flex items-center justify-center text-xs font-medium",
                                                                        isMapped
                                                                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                                                            : "bg-muted text-muted-foreground"
                                                                    )}>
                                                                        {isMapped ? <Check className="h-4 w-4" /> : field.label.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-sm">{field.label}</span>
                                                                            {isAutoMapped && (
                                                                                <Badge variant="secondary" className="text-[10px]">
                                                                                    Auto-mapped
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        {isMapped && (
                                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                                                                <span className="text-xs text-muted-foreground">
                                                                                    {isAutoMapped
                                                                                        ? getDisplayedAttioFieldLabel(field.name)
                                                                                        : getAttioFieldLabel(fieldMappings[field.name])}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {!isAutoMapped && isMapped && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={(e) => handleClearMapping(field.name, e)}
                                                                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                                                    >
                                                                        <X className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {isSelected && selectedField && !isAutoMapped && (
                                                            <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
                                                                <label className="block text-xs font-medium text-muted-foreground mb-2">
                                                                    Select Attio Attribute
                                                                </label>
                                                                <AttioFieldSelect
                                                                    fields={attioFields}
                                                                    value={fieldMappings[selectedField]}
                                                                    onSelect={handleAttioFieldSelect}
                                                                    placeholder="Search and select..."
                                                                    simpleDisplay
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>

                                <Separator />

                                <Collapsible
                                    open={expandedSections['system'] !== false}
                                    onOpenChange={() => toggleSection('system')}
                                >
                                    <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            {expandedSections['system'] === false ? (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <h4 className="font-medium">System Fields</h4>
                                            <Badge variant="outline" className="text-[10px]">
                                                {SYSTEM_FIELDS.length}
                                            </Badge>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            {Object.keys(fieldMappings).filter(k => SYSTEM_FIELDS.some(f => f.name === k)).length} mapped
                                        </span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="px-5 pb-5 space-y-2">
                                            <p className="text-xs text-muted-foreground mb-3">
                                                Automatically captured visitor data
                                            </p>
                                            {SYSTEM_FIELDS.map((field) => {
                                                const isMapped = Boolean(fieldMappings[field.name]);
                                                const isSelected = selectedField === field.name;
                                                return (
                                                    <div
                                                        key={field.name}
                                                        className={cn(
                                                            'rounded-lg border transition-all duration-200',
                                                            isSelected
                                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                                : isMapped
                                                                    ? 'border-emerald-500/30 bg-emerald-500/10'
                                                                    : 'hover:border-muted-foreground/30'
                                                        )}
                                                    >
                                                        <div className="p-3 cursor-pointer" onClick={() => handleFieldSelect(field.name)}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={cn(
                                                                        "h-8 w-8 rounded-lg flex items-center justify-center text-xs font-medium",
                                                                        isMapped
                                                                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                                                            : "bg-muted text-muted-foreground"
                                                                    )}>
                                                                        {isMapped ? <Check className="h-4 w-4" /> : field.label.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-medium text-sm">{field.label}</span>
                                                                        <p className="text-xs text-muted-foreground">{field.description}</p>
                                                                        {isMapped && (
                                                                            <div className="flex items-center gap-1.5 mt-1">
                                                                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                                                                <span className="text-xs text-emerald-600 dark:text-emerald-500">
                                                                                    {getAttioFieldLabel(fieldMappings[field.name])}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {isMapped && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={(e) => handleClearMapping(field.name, e)}
                                                                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                                                    >
                                                                        <X className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {isSelected && selectedField && (
                                                            <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
                                                                <label className="block text-xs font-medium text-muted-foreground mb-2">
                                                                    Select Attio Attribute
                                                                </label>
                                                                <AttioFieldSelect
                                                                    fields={attioFields}
                                                                    value={fieldMappings[selectedField]}
                                                                    onSelect={handleAttioFieldSelect}
                                                                    placeholder="Search and select..."
                                                                    simpleDisplay
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>

                                <Separator />

                                <Collapsible
                                    open={expandedSections['default'] !== false}
                                    onOpenChange={() => toggleSection('default')}
                                >
                                    <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            {expandedSections['default'] === false ? (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <h4 className="font-medium">Default Values</h4>
                                            {getDefaultCount() > 0 && (
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {getDefaultCount()}
                                                </Badge>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddDefaultField();
                                            }}
                                        >
                                            <Plus className="h-3.5 w-3.5 mr-1" />
                                            Add default
                                        </Button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="px-5 pb-5 space-y-3">
                                            {Object.entries(defaultFieldMappings).length === 0 ? (
                                                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                                                    No default values configured
                                                </div>
                                            ) : (
                                                Object.entries(defaultFieldMappings).map(([attioField, value]) => (
                                                    <AttioDefaultField
                                                        key={`${attioField}-${value}`}
                                                        attioField={attioField}
                                                        value={value}
                                                        onUpdate={handleDefaultFieldUpdate}
                                                        onRemove={() => handleRemoveDefaultField(attioField)}
                                                        availableFields={attioFields}
                                                    />
                                                ))
                                            )}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        </div>
                    )}
                </ScrollArea>

                <div className="border-t px-6 py-4 bg-background">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            {hasAnyConfiguration()
                                ? `${getMappedCount()} mapped fields${getDefaultCount() > 0 ? `, ${getDefaultCount()} defaults` : ''}`
                                : 'Name and email sync automatically. Add mappings only for extra Attio fields.'}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button onClick={handleSaveAll}>
                                Save Attio Sync
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
